import type {
  ProductUnitSystem,
  WeightedUnitSystem
} from "@kanpla/api-contract";

import type { Product, StepVariant } from "@repo/types";
import { stampcardActions } from "~/providers/store/stampcard";
import { autoId } from "~/utils/auto-id";

import { basketStore } from "./store";
import type {
  DiningOption,
  DiscountAmountType,
  ProductCustom,
  ProductVariant,
  StepVariantWeight
} from "./store.types";

function addRegular(productId: Product["id"], isHybridBilled = false) {
  basketStore.setState((state) => {
    const index = state.products.findIndex(
      (prod) =>
        prod.type === "regular" &&
        prod.productId === productId &&
        typeof prod.discountUnitPrice === "undefined" &&
        typeof prod.stampCardRuleId === "undefined" &&
        prod.isHybridBilled === isHybridBilled
    );

    if (index !== -1) {
      state.products[index].count += 1;
    } else {
      state.products.push({
        type: "regular",
        productId,
        id: autoId(),
        count: 1,
        isHybridBilled
      });
    }
  });
}

function addCustomProducts(
  customProducts: Omit<ProductCustom, "type" | "id">[]
) {
  basketStore.setState((state) => {
    for (const product of customProducts) {
      state.products.push({
        type: "custom",
        ...product,
        id: autoId()
      });
    }
  });
}

function editVariants(
  basketId: string,
  variants: StepVariant[],
  amount: number
) {
  basketStore.setState((state) => {
    const index = state.products.findIndex(
      (prod) => prod.id === basketId && prod.type === "variant"
    );

    if (index !== -1) {
      (state.products[index] as ProductVariant).options = variants;
      state.products[index].count = amount;
    }
  });
}

function addVariants(
  productId: Product["id"],
  variants: StepVariant[],
  amount: number,
  isHybridBilled = false
) {
  basketStore.setState((state) => {
    state.products.push({
      type: "variant",
      productId,
      id: autoId(),
      options: variants,
      count: amount,
      isHybridBilled
    });
  });
}

function setDiscount(productBasketId: string, discountUnitPrice: number) {
  basketStore.setState((state) => {
    if (discountUnitPrice === 0) {
      removeDiscount(productBasketId);
      return;
    }

    const index = state.products.findIndex(
      (prod) => prod.id === productBasketId
    );

    if (index !== -1) {
      state.products[index].discountUnitPrice = discountUnitPrice;
    }
  });
}

function removeDiscount(productBasketId: string) {
  basketStore.setState((state) => {
    const index = state.products.findIndex(
      (prod) => prod.id === productBasketId
    );

    if (index !== -1) {
      delete state.products[index].discountUnitPrice;
    }
  });
}

function increment(id: string) {
  basketStore.setState((state) => {
    const index = state.products.findIndex((prod) => prod.id === id);

    if (index !== -1) {
      state.products[index].count += 1;
    }
  });
}

function updateAmount(id: string, amount: number) {
  basketStore.setState((state) => {
    const index = state.products.findIndex((prod) => prod.id === id);

    if (index !== -1) {
      state.products[index].count = amount;
    }
  });
}

function remove(id: string) {
  basketStore.setState((state) => {
    const index = state.products.findIndex((prod) => prod.id === id);

    if (index !== -1) {
      state.products.splice(index, 1);
    }
  });
}

function reset() {
  basketStore.setState({
    products: [],
    orderDiscount: { amount: 0, type: "currency" },
    shopperName: "",
    isCardPaymentInProgress: false
  });
  stampcardActions.reset();
}

function setOrderDiscount(orderDiscount: {
  amount: number;
  type: DiscountAmountType;
}) {
  basketStore.setState({ orderDiscount });
}

function addVariantWeight(
  productId: Product["id"],
  variants: StepVariantWeight[],
  unitSystem: ProductUnitSystem | undefined,
  isHybridBilled = false
) {
  basketStore.setState((state) => {
    state.products.push({
      type: "variant-weight",
      productId,
      id: autoId(),
      options: variants,
      count: 1,
      unitSystem: unitSystem as WeightedUnitSystem,
      isHybridBilled
    });
  });
}

function addRedeemed(
  productId: Product["id"],
  discountUnitPrice: number,
  stampCardRuleId: string,
  variants?: StepVariant[]
) {
  basketStore.setState((state) => {
    state.products = state.products.filter(
      (product) => product.stampCardRuleId !== stampCardRuleId
    );

    if (variants) {
      state.products.push({
        type: "variant",
        productId,
        id: autoId(),
        options: variants,
        discountUnitPrice,
        count: 1,
        stampCardRuleId,
        isHybridBilled: false
      });
    } else {
      state.products.push({
        type: "regular",
        productId,
        id: autoId(),
        count: 1,
        discountUnitPrice,
        stampCardRuleId,
        isHybridBilled: false
      });
    }
  });
}

function removeRedeemed() {
  basketStore.setState((state) => {
    state.products = state.products.filter(
      (prod) => typeof prod.stampCardRuleId === "undefined"
    );
  });
}

/**
 * Update selected dining option
 * @param {string} option a text input value defined via the admin text input plugin
 */
function setDiningOption(diningOption: DiningOption) {
  basketStore.setState(() => ({ diningOption }));
}

/**
 * Reset selected dining option
 */
function resetDiningOption() {
  basketStore.setState(() => ({ diningOption: null }));
}

function setCardPaymentInProgress(newState: boolean) {
  basketStore.setState((state) => {
    state.isCardPaymentInProgress = newState;
  });
}

function setCardPaymentRecovering(newState: boolean) {
  basketStore.setState((state) => {
    state.isCardPaymentRecovering = newState;
  });
}

function setShopperName(shopperName: string) {
  basketStore.setState(() => ({ shopperName }));
}

function setActiveCategoryId(activeCategoryId: string) {
  basketStore.setState(() => ({ activeCategoryId }));
}

const basketActions = {
  addCustomProducts,
  addRegular,
  addVariants,
  updateAmount,
  editVariants,
  increment,
  remove,
  reset,
  setDiscount,
  removeDiscount,
  addVariantWeight,
  setOrderDiscount,
  setDiningOption,
  resetDiningOption,
  setCardPaymentInProgress,
  setCardPaymentRecovering,
  setShopperName,
  addRedeemed,
  removeRedeemed,
  setActiveCategoryId
};

export { basketActions };
